import { useEffect, useState } from 'react';
import { useI18n } from '../i18n';
import { useAccount } from '../account';
import { fetchTrivia, answerTrivia, type TriviaData, type TriviaReveal } from '../lib/api';
import { IconCheck } from '../components/Icons';

/**
 * 每日 Trivia —— 10 道服务端判分的选择题。
 *
 * 与其余五个模式最大的不同:**答错也有收获**。每题答完立刻揭晓 + 给一张事实卡片
 * (邻国国旗排 / 同货币国家 / 数值对比),所以这里的重点不是"猜对",是揭晓那一屏。
 *
 * 正确答案不在客户端 —— 未答的题里没有任何可推出答案的字段,选完才由服务端回揭晓。
 */
export default function TriviaMode() {
  const { t, lang } = useI18n();
  const { refresh } = useAccount();
  const [data, setData] = useState<TriviaData | null | undefined>(undefined);
  const [cur, setCur] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let on = true;
    setData(undefined);
    void fetchTrivia(lang).then((d) => {
      if (!on) return;
      setData(d);
      if (d) {
        const firstUn = d.questions.findIndex((q) => !q.answered);
        setCur(d.done || firstUn < 0 ? d.size : firstUn);
      }
    });
    return () => {
      on = false;
    };
  }, [lang]);

  async function choose(k: number) {
    if (!data || busy) return;
    const q = data.questions[cur];
    if (!q || q.answered) return;
    setBusy(true);
    const r = await answerTrivia(cur, k, lang);
    setBusy(false);
    if ('error' in r) return;
    setData((prev) => {
      if (!prev) return prev;
      const questions = prev.questions.slice();
      questions[cur] = { ...questions[cur], answered: { chosen: k, reveal: r.reveal } };
      return { ...prev, questions, score: r.score, done: r.done };
    });
    if (r.done && r.xp > 0) void refresh(); // 服务端已发 XP,刷新等级胶囊
  }

  if (data === undefined) return <div className="trivia"><p className="empty-hint">{t.trivia.loading}</p></div>;
  if (data === null) return <div className="trivia"><p className="empty-hint">{t.netError}</p></div>;

  const total = data.size;
  const done = data.done || cur >= total;

  const dots = (
    <div className="tv-dots" aria-label={`${data.score}/${total}`}>
      {data.questions.map((q, i) => {
        const st = q.answered
          ? q.answered.chosen === q.answered.reveal.correctIndex ? 'ok' : 'no'
          : i === cur && !done ? 'cur' : '';
        return <span key={i} className={`tv-dot ${st}`} />;
      })}
    </div>
  );

  if (done) {
    const tier = data.score >= total ? 0 : data.score >= 7 ? 1 : data.score >= 4 ? 2 : 3;
    return (
      <div className="trivia">
        {dots}
        <div className="tv-result">
          <div className="tv-score">
            <b>{data.score}</b>
            <span>/ {total}</span>
          </div>
          <div className="tv-tier">{t.trivia.tiers[tier]}</div>
          {/* 不显示 XP 的话,用户会以为 Trivia 不给经验 */}
          {data.xp > 0 && (
            <div className="tv-xp">
              +{data.xp} {t.xp}
              <i>{t.trivia.xpNote}</i>
            </div>
          )}
          <p className="tv-sub">{t.trivia.comeBack}</p>
        </div>
      </div>
    );
  }

  const q = data.questions[cur];
  const ans = q.answered;

  return (
    <div className="trivia">
      {dots}
      <div className="tv-meta">
        {t.trivia.question} {cur + 1} / {total}
      </div>
      <h2 className="tv-stem">{q.stem}</h2>

      {/* 选项都很短时走两列 —— 四个 6 字以内的城市名占四整行是浪费 */}
      <div className={`tv-opts${q.options.every((o) => o.length <= 14) ? ' two' : ''}`}>
        {q.options.map((o, k) => {
          let cls = 'tv-opt';
          if (ans) {
            if (k === ans.reveal.correctIndex) cls += ' correct';
            else if (k === ans.chosen) cls += ' wrong';
            else cls += ' dim';
          }
          return (
            <button key={k} className={cls} disabled={!!ans || busy} onClick={() => choose(k)}>
              <span className="tv-key">{String.fromCharCode(65 + k)}</span>
              <span className="tv-txt">{o}</span>
              {ans && k === ans.reveal.correctIndex && <IconCheck className="tv-mark" width={14} height={14} />}
            </button>
          );
        })}
      </div>

      {ans && (
        <div className={`tv-reveal ${ans.chosen === ans.reveal.correctIndex ? 'ok' : 'no'}`}>
          <b className="tv-verdict">{ans.chosen === ans.reveal.correctIndex ? t.trivia.correct : t.trivia.wrong}</b>
          <RevealCard r={ans.reveal} />
          <button className="conn-btn primary tv-next" onClick={() => setCur((c) => Math.min(c + 1, total))}>
            {cur + 1 >= total ? t.trivia.seeResult : t.trivia.next}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * 揭晓卡片。**主体是答案本身**,不是千篇一律的"国旗 + 国名 + 一行小字":
 * 首都题的主体是首都、语言题是语言、人物题是**那个人**(国家降为一枚带「来自」标题的旗)。
 * 国旗排一律带标题 —— 没有标题的一排旗子,用户不知道你想说什么。
 */
function RevealCard({ r }: { r: TriviaReveal }) {
  const max = r.bars ? Math.max(...r.bars.map((b) => b.value), 1) : 1;
  return (
    <div className="tv-card">
      <div className="tv-lead-row">
        {r.flag && <img className="tv-flag" src={`/assets/flags/${r.flag}.svg`} alt="" />}
        <span className="tv-lead-txt">
          <b className="tv-lead">{r.lead}</b>
          {r.sub && <i className="tv-sub2">{r.sub}</i>}
        </span>
      </div>

      {r.chips && r.chips.length > 0 && (
        <div className="tv-chipbox">
          {r.chipsLabel && <span className="tv-chiplabel">{r.chipsLabel}</span>}
          <div className="tv-chips">
            {r.chips.map((c) => (
              <span className="tv-chip" key={c.cca2}>
                <img src={`/assets/flags/${c.cca2}.svg`} alt="" />
                {c.name}
              </span>
            ))}
            {!!r.more && <span className="tv-more">+{r.more}</span>}
          </div>
        </div>
      )}

      {r.bars && (
        <div className="tv-bars">
          {r.bars.map((b) => (
            <div className="tv-bar" key={b.name}>
              <span className="tv-bn">{b.name}</span>
              <span className="tv-bt"><i style={{ width: `${Math.max(2, (b.value / max) * 100)}%` }} /></span>
              <span className="tv-bv">{b.value.toLocaleString()}{b.unit ? ` ${b.unit}` : ''}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
